import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';

interface UserRow {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'analyst';
  active: number;
  created_at: string;
}

export function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'analyst'>('analyst');

  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState<'admin' | 'analyst'>('analyst');
  const [editPassword, setEditPassword] = useState('');

  const load = async () => {
    const data = await api.get<{ users: UserRow[] }>('/api/users');
    setUsers(data.users);
  };

  useEffect(() => {
    if (user?.role !== 'admin') return;
    void load().catch((e) => setError(e instanceof Error ? e.message : 'Laden mislukt'));
  }, [user?.role]);

  if (user?.role !== 'admin') return <Navigate to="/" replace />;

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api.post('/api/users', { email, name, password, role });
      setEmail('');
      setName('');
      setPassword('');
      setRole('analyst');
      setInfo('Gebruiker aangemaakt.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aanmaken mislukt');
    }
  };

  const startEdit = (u: UserRow) => {
    setEditId(u.id);
    setEditName(u.name);
    setEditEmail(u.email);
    setEditRole(u.role);
    setEditPassword('');
    setError(null);
  };

  const saveEdit = async () => {
    if (!editId) return;
    setError(null);
    try {
      const body: Record<string, unknown> = {
        name: editName,
        email: editEmail,
        role: editRole,
      };
      if (editPassword.trim()) body.password = editPassword.trim();
      await api.patch(`/api/users/${editId}`, body);
      setEditId(null);
      setInfo('Gebruiker bijgewerkt.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    }
  };

  const toggleActive = async (u: UserRow) => {
    setError(null);
    try {
      await api.patch(`/api/users/${u.id}`, { active: !u.active });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status wijzigen mislukt');
    }
  };

  const remove = async (u: UserRow) => {
    if (!confirm(`Gebruiker ${u.name} (${u.email}) definitief verwijderen?`)) return;
    setError(null);
    try {
      await api.delete(`/api/users/${u.id}`);
      setInfo('Gebruiker verwijderd.');
      if (editId === u.id) setEditId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    }
  };

  return (
    <div>
      <h1>Gebruikers</h1>
      <p className="page-lead">Beheer accounts voor het onderzoeksteam. Rollen: admin of analist.</p>

      <form className="panel toolbar" onSubmit={(e) => void create(e)}>
        <div className="field">
          <label>Naam</label>
          <input className="input input-md" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label>E-mail</label>
          <input
            className="input input-md"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label>Wachtwoord</label>
          <input
            className="input input-md"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
        </div>
        <div className="field">
          <label>Rol</label>
          <select className="select input-md" value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'analyst')}>
            <option value="analyst">Analist</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <button className="btn" type="submit">
          Gebruiker toevoegen
        </button>
      </form>

      {error && <div className="alert error">{error}</div>}
      {info && <div className="alert ok">{info}</div>}

      <div className="panel" style={{ marginTop: 14, padding: 0, overflow: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Naam</th>
              <th>E-mail</th>
              <th>Rol</th>
              <th>Status</th>
              <th>Aangemaakt</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) =>
              editId === u.id ? (
                <tr key={u.id}>
                  <td>
                    <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} />
                  </td>
                  <td>
                    <input className="input" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                  </td>
                  <td>
                    <select
                      className="select input-md"
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value as 'admin' | 'analyst')}
                    >
                      <option value="analyst">Analist</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td colSpan={2}>
                    <input
                      className="input"
                      type="password"
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                      placeholder="Nieuw wachtwoord (optioneel)"
                      minLength={8}
                    />
                  </td>
                  <td>
                    <div className="row">
                      <button className="btn btn-sm" type="button" onClick={() => void saveEdit()}>
                        Opslaan
                      </button>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => setEditId(null)}>
                        Annuleren
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{u.role}</td>
                  <td>
                    <span className={`badge ${u.active ? 'ok' : 'danger'}`}>
                      {u.active ? 'actief' : 'inactief'}
                    </span>
                  </td>
                  <td className="muted">{formatDateTime(u.created_at)}</td>
                  <td>
                    <div className="row" style={{ flexWrap: 'wrap' }}>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => startEdit(u)}>
                        Bewerken
                      </button>
                      {u.id !== user.id && (
                        <>
                          <button className="btn btn-ghost btn-sm" type="button" onClick={() => void toggleActive(u)}>
                            {u.active ? 'Deactiveren' : 'Activeren'}
                          </button>
                          <button className="btn btn-danger btn-sm" type="button" onClick={() => void remove(u)}>
                            Verwijderen
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
